import { OrdenPrestador } from 'src/app/models/OrdenPrestador';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { API_URI } from 'src/environments/environment';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-order-response-prestador',
    templateUrl: './order-response-prestador.component.html',
    styleUrls: ['./order-response-prestador.component.css'],
    standalone: false
})
export class OrderResponsePrestadorComponent {
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private _router = inject(Router);
  private _location = inject(Location);
  private _aRoute = inject(ActivatedRoute);

  error = signal<string>('');
  loadingGlobal = computed(() => this._loadingService.isLoading());
  loadingLocal = signal<boolean>(false);
  isFinished = signal<boolean>(false);

  id: number = Number(this._aRoute.snapshot.paramMap.get('id'));

  ordenPrestador?: OrdenPrestador;
  preview_img_avatar?: string;
  url_perfil_cliente?: string;
  nombreCliente?: string;
  nombreEspecialidad?: string;
  nombrePrestacion?: string;
  
  d_fecha_uno_prestador?: string;
  d_fecha_dos_prestador?: string;
  d_fecha_tres_prestador?: string;

  n_precio_turno: number | null = null;

  // placeholders / visibilidad inputs datetime
  showFechaUnoInput = signal<boolean>(false);
  showFechaDosInput = signal<boolean>(false);
  showFechaTresInput = signal<boolean>(false);

  readOnly = signal(false);

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        if (this._authService.session() === 'guest') {
          this._router.navigate(['/sign-in']);
          return;
        }

        if (!this.id) {
          this._router.navigate(['/dashboard']);
          return;
        }

        const resOrden = await firstValueFrom(this._orderPrestadorService.getOrdenPrestador(this.id));
        if (!resOrden) {
          this._router.navigate(['/dashboard']);
          return;
        }

        const perfil = this._usuarioService.usuario().id_perfil;
        const estado = resOrden.id_estado_orden ?? 0;

        if (perfil === 3 && estado >= 2) {
          this.readOnly.set(true);
        }

        if ((resOrden.id_estado_orden ?? 0) > 1 || this._usuarioService.usuario().id_perfil === 2) {
          //this._router.navigate(['/order-confirm-prestador/' + this.id]);
          //return;
        }

        this.ordenPrestador = resOrden;
        this.url_perfil_cliente = '/perfil-cliente/' + this.ordenPrestador.id_cliente!;
        this.preview_img_avatar = this.ordenPrestador.imgAvatarCliente
          ? API_URI + 'uploads/images/small-' + this.ordenPrestador.imgAvatarCliente
          : '';
        this.nombreCliente = this.ordenPrestador.nombreCliente ?? '';
        this.nombreEspecialidad = this.ordenPrestador.nombreEspecialidad ?? '';
        this.nombrePrestacion = this.ordenPrestador.nombrePrestacion ?? '';

        // Si la orden ya tenía propuestas cargadas, las mostramos
        if (this.ordenPrestador.d_fecha_uno_prestador) {
          this.d_fecha_uno_prestador = this.toDatetimeLocal(this.ordenPrestador.d_fecha_uno_prestador);
          this.showFechaUnoInput.set(true);
        }
        if (this.ordenPrestador.d_fecha_dos_prestador) {
          this.d_fecha_dos_prestador = this.toDatetimeLocal(this.ordenPrestador.d_fecha_dos_prestador);
          this.showFechaDosInput.set(true);
        }
        if (this.ordenPrestador.d_fecha_tres_prestador) {
          this.d_fecha_tres_prestador = this.toDatetimeLocal(this.ordenPrestador.d_fecha_tres_prestador);
          this.showFechaTresInput.set(true);
        }

        if (this.ordenPrestador.n_precio_turno != null) {
          this.n_precio_turno = Number(this.ordenPrestador.n_precio_turno);
        }
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar el detalle del turno.');
      }
    });
  }

  async finalizaResponseOrder(): Promise<void> {
    this.error.set('');
    this.loadingLocal.set(true);

    try {
      if (!this.ordenPrestador) {
        this.error.set('No se pudo cargar la orden.');
        return;
      }

      if (!this.n_precio_turno || this.n_precio_turno <= 0) {
        this.error.set('Debe completar el precio del turno');
        return;
      }

      if (!this.d_fecha_uno_prestador) {
        this.error.set('Debe ingresar Fecha y Hora para la Propuesta 1');
        return;
      }

      if (!this.d_fecha_dos_prestador) {
        this.error.set('Debe ingresar Fecha y Hora para la Propuesta 2');
        return;
      }

      if (!this.d_fecha_tres_prestador) {
        this.error.set('Debe ingresar Fecha y Hora para la Propuesta 3');
        return;
      }

      const fecha1 = new Date(this.d_fecha_uno_prestador);
      const fecha2 = new Date(this.d_fecha_dos_prestador);
      const fecha3 = new Date(this.d_fecha_tres_prestador);

      if (isNaN(fecha1.getTime()) || isNaN(fecha2.getTime()) || isNaN(fecha3.getTime())) {
        this.error.set('Las fechas propuestas no son válidas.');
        return;
      }

      const ahora = new Date();

      if (fecha1 <= ahora || fecha2 <= ahora || fecha3 <= ahora) {
        this.error.set('Las fechas propuestas no pueden ser pasadas.');
        return;
      }

      const f1 = fecha1.getTime();
      const f2 = fecha2.getTime();
      const f3 = fecha3.getTime();

      if (f1 === f2 || f1 === f3 || f2 === f3) {
        this.error.set('Las fechas propuestas no pueden repetirse.');
        return;
      }

      this.ordenPrestador.n_precio_turno = this.n_precio_turno;
      this.ordenPrestador.d_fecha_uno_prestador = fecha1;
      this.ordenPrestador.d_fecha_dos_prestador = fecha2;
      this.ordenPrestador.d_fecha_tres_prestador = fecha3;

      await this._loadingService.run(async () => {
        await this._orderPrestadorService.responseOrdenPrestador(this.ordenPrestador!);
        this.isFinished.set(true);
      });
    } catch (error: any) {
      if (error?.codigo && error.codigo === 1) {
        this.error.set(error.error);
      } else {
        this.error.set('Lo sentimos ha ocurrido un error.');
      }
    } finally {
      this.loadingLocal.set(false);
    }
  }

  // Validar entrada para permitir solo números y un punto decimal
  onPrecioInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;

    // Reemplazar todas las comas por puntos
    inputElement.value = inputElement.value.replace(/,/g, '.');

    // Reemplazar caracteres no numéricos (excepto el punto decimal)
    inputElement.value = inputElement.value.replace(/[^0-9.]/g, '');

    // Controlar que solo haya un punto decimal
    const parts = inputElement.value.split('.');
    if (parts.length > 2) {
      inputElement.value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Actualizar el valor como número (o null si está vacío)
    this.n_precio_turno = inputElement.value ? parseFloat(inputElement.value) : null;
  }

  // Formatear el precio al perder el foco
  formatPrecio(): void {
    if (this.n_precio_turno !== null) {
      this.n_precio_turno = parseFloat(this.n_precio_turno.toFixed(2));
    }
  }

  back(): void {
    this._location.back();
  }

  openFile(): void {
    const x_img_receta = this.ordenPrestador?.x_img_receta;
    if (!x_img_receta) return;
    window.open(`${API_URI}uploads/images/${x_img_receta}`, '_blank');
  }

  showFechaInput(idplace: number): void {
    if (idplace === 1) this.showFechaUnoInput.set(true);
    if (idplace === 2) this.showFechaDosInput.set(true);
    if (idplace === 3) this.showFechaTresInput.set(true);
  }

  onDateChange(): void {
    // Opcional para manejar el cambio de fecha
  }

  private toDatetimeLocal(value: Date | string): string {
    const d = new Date(value);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  
}